import {
  MdFlight,
  MdEdit,
  MdDelete,
  MdVisibility,
  MdWarning,
  MdCheckCircle,
  MdSchedule,
  MdLocationOn,
  MdBusiness,
} from 'react-icons/md'
import { Badge } from '../../components/ui'

const AircraftList = ({ aircraft, onViewAircraft, onEditAircraft, onDeleteAircraft }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success'
      case 'inactive':
        return 'warning'
      case 'maintenance':
        return 'error'
      case 'retired':
        return 'neutral'
      default:
        return 'neutral'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <MdCheckCircle className="text-success-600 dark:text-success-400" />
      case 'inactive':
        return <MdWarning className="text-warning-600 dark:text-warning-400" />
      case 'maintenance':
        return <MdSchedule className="text-error-600 dark:text-error-400" />
      case 'retired':
        return <MdFlight className="text-neutral-600 dark:text-neutral-400" />
      default:
        return <MdFlight className="text-neutral-600 dark:text-neutral-400" />
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }

  return (
    <div className="space-y-4">
      {aircraft.map((aircraft) => (
        <div
          key={aircraft._id}
          className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Aircraft Basic Info */}
            <div className="flex-1">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
                      {aircraft.name}
                    </h3>
                    <Badge variant={getStatusColor(aircraft.status)}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(aircraft.status)}
                        <span className="capitalize">{aircraft.status}</span>
                      </div>
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-neutral-600 dark:text-neutral-400">
                    <div className="flex items-center gap-1">
                      <MdFlight className="text-lg" />
                      <span>{aircraft.code}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdBusiness className="text-lg" />
                      <span>{aircraft.airline?.name}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MdLocationOn className="text-lg" />
                      <span>{aircraft.operationalInfo?.currentLocation || 'Unknown'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Aircraft Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Aircraft Info
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>
                      {aircraft.maker} {aircraft.model}
                    </div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {aircraft.type} • {aircraft.year}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Registration
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{aircraft.registrationNumber}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      Serial: {aircraft.serialNumber}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Configuration
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{aircraft.configuration?.totalSeats} seats</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {aircraft.specifications?.engines?.count} engines
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-1">
                    Value & Status
                  </div>
                  <div className="text-sm text-neutral-900 dark:text-white">
                    <div>{formatCurrency(aircraft.currentValue)}</div>
                    <div className="text-neutral-600 dark:text-neutral-400">
                      {aircraft.maintenance?.maintenanceStatus}
                    </div>
                  </div>
                </div>
              </div>

              {/* Operational Info */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-neutral-600 dark:text-neutral-400">
                <span>
                  Flight Hours: {aircraft.operationalInfo?.totalFlightHours?.toLocaleString()}
                </span>
                <span>Cycles: {aircraft.operationalInfo?.totalCycles?.toLocaleString()}</span>
                <span>Utilization: {aircraft.operationalInfo?.utilizationRate?.toFixed(1)}%</span>
                <span>
                  Last Flight:{' '}
                  {aircraft.operationalInfo?.lastFlight
                    ? formatDate(aircraft.operationalInfo.lastFlight)
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 lg:flex-col">
              <button
                onClick={() => onViewAircraft(aircraft)}
                className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
                title="View Details"
              >
                <MdVisibility className="text-lg" />
              </button>
              <button
                onClick={() => onEditAircraft(aircraft)}
                className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-warning-600 dark:hover:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-lg transition-colors duration-200"
                title="Edit Aircraft"
              >
                <MdEdit className="text-lg" />
              </button>
              <button
                onClick={() => onDeleteAircraft(aircraft)}
                className="p-2 text-neutral-600 dark:text-neutral-400 hover:text-error-600 dark:hover:text-error-400 hover:bg-error-50 dark:hover:bg-error-900/20 rounded-lg transition-colors duration-200"
                title="Delete Aircraft"
              >
                <MdDelete className="text-lg" />
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

export default AircraftList
